import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Box,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
  Divider
} from '@mui/material';
import { AutoAwesome, ContentCopyOutlined } from '@mui/icons-material';
import milestoneService from '../../services/milestoneService';
import api from '../../services/api';
import DialogCloseButton from '../common/DialogCloseButton';
import { brand } from '../../theme/colors';

const getInitialFormData = () => ({
  title: '',
  description: '',
  expectedPhotos: '',
  expectedDocuments: '',
  duration: '',
  durationUnit: 'weeks',
  stageAmount: '',
  startDate: '',
  endDate: ''
});

const SAMPLE_STAGE = {
  title: 'Siting, Excavation and Foundation',
  description: [
    'Set out the house footprint from the approved plan, clear the work area, excavate foundation trenches, place formwork and reinforcement, pour concrete, cure, backfill, and compact.',
    'Owner approval is based on the stage matching the agreed drawings, correct trench depth and alignment, visible reinforcement before concrete pour, clean curing/backfill, and no unresolved defects.'
  ].join('\n\n'),
  expectedPhotos: [
    'Site before clearing and after setting-out lines are marked',
    'Excavated trenches showing depth, width, and alignment',
    'Reinforcement and formwork before concrete pour',
    'Concrete pour in progress and completed cured foundation',
    'Backfilled and compacted foundation area'
  ].join('\n'),
  expectedDocuments: [
    'Stage BOQ / materials and labour schedule',
    'Concrete or material delivery notes/receipts',
    'Engineer, inspector, or supervisor sign-off if required',
    'Any variation notes approved by the owner'
  ].join('\n'),
  duration: '4',
  durationUnit: 'weeks',
  stageAmount: ''
};

const MilestoneCreationDialog = ({ open, onClose, projectId, onMilestoneCreated }) => {
  const [formData, setFormData] = useState({
    ...getInitialFormData()
  });
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Stage title is required');
      return false;
    }

    if (formData.title.length < 3) {
      setError('Stage title must be at least 3 characters');
      return false;
    }
    
    if (formData.duration && formData.duration <= 0) {
      setError('Duration must be greater than 0');
      return false;
    }

    if (formData.stageAmount && parseFloat(formData.stageAmount) <= 0) {
      setError('Stage amount must be greater than 0');
      return false;
    }
    
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (end <= start) {
        setError('End date must be after start date');
        return false;
      }
    }
    
    return true;
  };

  const splitLines = (value) => value
    .split('\n')
    .map(item => item.trim())
    .filter(Boolean);

  const buildStageDescription = () => {
    const sections = [formData.description.trim()];
    const expectedPhotos = splitLines(formData.expectedPhotos);
    const expectedDocuments = splitLines(formData.expectedDocuments);

    if (expectedPhotos.length || expectedDocuments.length) {
      sections.push([
        'Completion evidence expected before owner approval:',
        ...expectedPhotos.map(item => `Photo: ${item}`),
        ...expectedDocuments.map(item => `Document: ${item}`)
      ].join('\n'));
    }

    return sections.filter(Boolean).join('\n\n');
  };

  const handleUseSampleStage = () => {
    setFormData(prev => ({
      ...prev,
      ...SAMPLE_STAGE
    }));
    if (error) setError('');
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create milestone — send in the shape the backend expects
      await milestoneService.createMilestone({
        projectId,
        milestoneDetails: {
          name: formData.title,
          description: buildStageDescription(),
          type: 'general',
        },
        requirements: {
          verificationRequired: true,
          documentRequirements: [
            ...splitLines(formData.expectedPhotos).map(item => `Photo: ${item}`),
            ...splitLines(formData.expectedDocuments).map(item => `Document: ${item}`)
          ],
        },
        financial: {
          budgetAllocation: formData.stageAmount ? parseFloat(formData.stageAmount) : 0,
        },
        scheduling: {
          plannedStartDate: formData.startDate || null,
          plannedEndDate: formData.endDate || null,
        },
      });

      // Reset form
      setFormData(getInitialFormData());

      // Call success callback and let the parent refresh the visible stage list.
      await onMilestoneCreated?.();
    } catch (err) {
      console.error('Error creating milestone:', err);
      setError(err.message || 'Failed to create milestone. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (!formData.title.trim()) {
      setError('Enter a milestone title first so AI can generate a description');
      return;
    }
    setAiLoading(true);
    try {
      const response = await api.post('/ai/generate-text', {
        prompt: `Write a concise 2-3 sentence description for a construction work stage called "${formData.title}". Include key deliverables, acceptance criteria, and what the contractor should accomplish before asking the owner to approve this stage. Be specific and professional.`,
        maxTokens: 150
      });
      if (response.data?.data?.text) {
        setFormData(prev => ({ ...prev, description: response.data.data.text.trim() }));
      }
    } catch (err) {
      // Fallback: generate a simple description locally
      const descriptions = {
        'tiling': 'Supply and install all floor and wall tiles as per design specifications. Includes surface preparation, waterproofing where required, grouting, and cleanup. All materials and labor included.',
        'plumbing': 'Complete plumbing installation including water supply lines, drainage, fixtures, and connections. Includes pressure testing, leak checks, and compliance with local building codes.',
        'electrical': 'Full electrical installation including wiring, outlets, switches, lighting fixtures, and distribution board. Includes testing, certification, and compliance with safety standards.',
        'painting': 'Interior and exterior painting including surface preparation, priming, and two coats of finish paint. All walls, ceilings, and trim as specified in the project scope.',
        'foundation': 'Excavation, formwork, steel reinforcement, and concrete pouring for building foundation. Includes compaction, damp proofing, and curing period.',
        'roofing': 'Complete roof installation including trusses, battens, roofing material, ridge caps, and waterproofing. Includes fascia boards and gutter installation.',
      };
      const key = Object.keys(descriptions).find(k => formData.title.toLowerCase().includes(k));
      setFormData(prev => ({
        ...prev,
        description: key ? descriptions[key] : `Complete all work related to ${formData.title} as per project specifications. Includes materials, labor, cleanup, and evidence for owner approval. Work must meet quality standards and comply with local building regulations.`
      }));
    } finally {
      setAiLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData(getInitialFormData());
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogCloseButton onClick={handleClose} />
      <DialogTitle
        sx={{
          background: brand.gradient,
          color: 'white',
          p: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 1, mb: 1 }}>
          Create Work Stage
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Define a project stage with timeline, scope, and payment details for owner approval
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Alert
                severity="info"
                sx={{
                  mb: 2,
                  alignItems: 'center',
                  '& .MuiAlert-message': { width: '100%' }
                }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    startIcon={<ContentCopyOutlined />}
                    onClick={handleUseSampleStage}
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Use Sample
                  </Button>
                }
              >
                Need a starting point? Use the sample foundation stage, then adjust the scope, evidence, timing, and payment percentage.
              </Alert>
            <TextField
              label="Stage Title"
              fullWidth
              required
              value={formData.title}
              onChange={handleChange('title')}
              placeholder="e.g., Siting, Excavation and Foundation"
              helperText="A clear, descriptive name for this work stage"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Stage scope and acceptance criteria"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder="Describe the work, deliverables, and what the owner should check before approving this stage..."
              helperText="Include the work to be done and acceptance criteria. Add required photos/documents below."
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                onClick={handleGenerateDescription}
                disabled={aiLoading || !formData.title.trim()}
                sx={{
                  background: brand.gradient,
                  textTransform: 'none',
                  fontWeight: 'bold',
                  '&:hover': { background: brand.gradientHover }
                }}
              >
                {aiLoading ? 'Improving...' : 'Improve with AI'}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Required completion evidence
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These files are not uploaded now. The contractor will upload them later when submitting the stage completion report.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Photos to upload at completion"
                    fullWidth
                    multiline
                    minRows={4}
                    value={formData.expectedPhotos}
                    onChange={handleChange('expectedPhotos')}
                    placeholder={'e.g., Before excavation\nReinforcement before concrete pour\nCompleted foundation after curing'}
                    helperText="One expected photo per line"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Documents to upload at completion"
                    fullWidth
                    multiline
                    minRows={4}
                    value={formData.expectedDocuments}
                    onChange={handleChange('expectedDocuments')}
                    placeholder={'e.g., Stage BOQ\nMaterial receipts\nInspector sign-off'}
                    helperText="One expected document per line"
                  />
                </Grid>
              </Grid>
            </Grid>

          <Grid item xs={6}>
            <TextField
              label="Duration (optional)"
              type="number"
              fullWidth
              value={formData.duration}
              onChange={handleChange('duration')}
              inputProps={{ min: 1, step: 1 }}
              helperText="How long will this stage take?"
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Unit"
              select
              fullWidth
              required
              value={formData.durationUnit}
              onChange={handleChange('durationUnit')}
            >
              <MenuItem value="days">Days</MenuItem>
              <MenuItem value="weeks">Weeks</MenuItem>
              <MenuItem value="months">Months</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Stage amount"
              type="number"
              fullWidth
              value={formData.stageAmount}
              onChange={handleChange('stageAmount')}
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              helperText="Agreed with the client. This amount is locked into escrow before the stage starts and released to the contractor when the stage is approved."
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Timeline (Optional)
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Estimated Start Date"
              type="date"
              fullWidth
              value={formData.startDate}
              onChange={handleChange('startDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Estimated End Date"
              type="date"
              fullWidth
              value={formData.endDate}
              onChange={handleChange('endDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading}
          sx={{
            background: brand.gradient,
            '&:hover': {
              background: brand.gradientHover
            }
          }}
        >
          {loading ? 'Creating...' : 'Create Stage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MilestoneCreationDialog;
